import {
  CampaignHealthScoreSchema,
  AvailableCitationsResponseSchema,
  CampaignEstimateSchema,
  CampaignLookupResultSchema,
  CampaignPackagesResponseSchema,
  ConfirmCampaignOrderResponseSchema,
  CreditBalanceResponseSchema,
  CreateCitationBuilderResponseSchema,
  FindCampaignsResponseSchema,
  GetCampaignSchema,
  PatchCampaignOrderResponseSchema,
  PublisherPricesResponseSchema,
  SubscribeLookupNotificationResponseSchema,
  type ConfirmCampaignOrderParams,
  type CreateCampaignParams,
  type FindCampaignsParams,
  type GetCampaignHealthScoreParams,
  type GetAvailableCitationsParams,
  type GetCampaignEstimateParams,
  type GetCampaignLookupResultParams,
  type GetCampaignParams,
  type GetCampaignPackagesParams,
  type GetPublisherPricesParams,
  type PatchCampaignOrderParams,
  type SubscribeLookupNotificationParams
} from './schema';
import { HTTP_METHOD } from '../../consts';
import { serializeRequestBody } from '../../serializer';
import type { ApiCallOptions, Transport } from '../../types';

export const createCitationBuilderApi = (transport: Transport) => ({
  /** Creates a citation builder campaign for the given location. */
  createCampaign: ({ locationId, source }: CreateCampaignParams, options?: ApiCallOptions) =>
    transport('/manage/v1/citation-builder', {
      method: HTTP_METHOD.POST,
      body: serializeRequestBody({ locationId, source }),
      deserializer: CreateCitationBuilderResponseSchema,
      signal: options?.signal
    }),

  /**
   * Lists citation-builder campaigns matching the filter. Used by the entry
   * route to detect an existing draft before calling `createCampaign`.
   */
  findCampaigns: ({ locationId }: FindCampaignsParams = {}, options?: ApiCallOptions) =>
    transport('/manage/v1/citation-builder', {
      method: HTTP_METHOD.GET,
      queryParams: { location_id: locationId },
      deserializer: FindCampaignsResponseSchema,
      signal: options?.signal
    }),

  /**
   * Returns the lookup (scan) result for a campaign. While the worker is still
   * running, `lookup_status` is `processing` and `citations` is empty/null;
   * once finished, status flips to `complete` and the citations array is
   * populated. Consumed by the scan-step polling hook.
   */
  getCampaignLookupResult: ({ campaignId }: GetCampaignLookupResultParams, options?: ApiCallOptions) =>
    transport(`/manage/v1/citation-builder/${campaignId}/lookup`, {
      method: HTTP_METHOD.GET,
      deserializer: CampaignLookupResultSchema,
      signal: options?.signal
    }),

  /** Returns the citation health score (0–100 index + directory breakdown) for a campaign. */
  getCampaignHealthScore: ({ campaignId }: GetCampaignHealthScoreParams, options?: ApiCallOptions) =>
    transport(`/manage/v1/citation-builder/${campaignId}/health`, {
      method: HTTP_METHOD.GET,
      deserializer: CampaignHealthScoreSchema,
      signal: options?.signal
    }),

  /**
   * Registers an email to be notified when the lookup for a campaign completes.
   * The BE returns 409 if the email is already registered for this campaign —
   * callers should treat that as idempotent success.
   */
  subscribeLookupNotification: (
    { campaignId, email, platform }: SubscribeLookupNotificationParams,
    options?: ApiCallOptions
  ) =>
    transport(`/manage/v1/citation-builder/${campaignId}/lookup-notification`, {
      method: HTTP_METHOD.POST,
      body: serializeRequestBody({ email, platform }),
      deserializer: SubscribeLookupNotificationResponseSchema,
      signal: options?.signal
    }),

  /**
   * Lists directories available for the campaign with their domain authority
   * and feature flags (phone-verification required, instant listing, etc.).
   * Consumed by the Fix step to enrich lookup rows with DA badges.
   */
  getAvailableCitations: ({ campaignId, page, numPerPage }: GetAvailableCitationsParams, options?: ApiCallOptions) =>
    transport(`/manage/v1/citation-builder/${campaignId}/citations`, {
      method: HTTP_METHOD.GET,
      queryParams: { page, num_per_page: numPerPage },
      deserializer: AvailableCitationsResponseSchema,
      signal: options?.signal
    }),

  /** The campaign's country-scoped publisher price list + bundle discount ladder (CB-4483). */
  getPublisherPrices: ({ campaignId }: GetPublisherPricesParams, options?: ApiCallOptions) =>
    transport(`/manage/v1/citation-builder/${campaignId}/publishers`, {
      method: HTTP_METHOD.GET,
      deserializer: PublisherPricesResponseSchema,
      signal: options?.signal
    }),

  /**
   * The customer's remaining Citation Builder credit balance. Account-scoped
   * (resolved from the API key's customer), so it takes no campaign id — the
   * Review step reads it to show the balance and gate pay-with-credits.
   */
  getCreditBalance: (options?: ApiCallOptions) =>
    transport('/manage/v1/citation-builder/credits', {
      method: HTTP_METHOD.GET,
      deserializer: CreditBalanceResponseSchema,
      signal: options?.signal
    }),

  /**
   * Lists the citation packages available for the campaign (per-customer
   * catalog resolved BE-side). The list has no "default" flag and is sorted
   * ascending by citation count; the Fix step picks the default itself and
   * uses it to price the estimate before the customer reaches the bundle step.
   */
  getCampaignPackages: (
    { campaignId, page, numPerPage, hideAddress }: GetCampaignPackagesParams,
    options?: ApiCallOptions
  ) =>
    transport(`/manage/v1/citation-builder/${campaignId}/packages`, {
      method: HTTP_METHOD.GET,
      queryParams: { page, num_per_page: numPerPage, hide_address: hideAddress },
      deserializer: CampaignPackagesResponseSchema,
      signal: options?.signal
    }),

  /**
   * Returns the estimated cost for a campaign. Query params override the
   * stored campaign state ad-hoc (no save required), so consumers can
   * preview pricing as the customer toggles bundle size, duplicate
   * removal, express, active sync, or aggregator selection.
   */
  getCampaignEstimate: (
    { campaignId, packageName, removeDuplicates, isExpress, isActiveSync, publishers }: GetCampaignEstimateParams,
    options?: ApiCallOptions
  ) =>
    transport(`/manage/v1/citation-builder/${campaignId}/estimate`, {
      method: HTTP_METHOD.GET,
      // The BE takes `publishers` as a comma-separated list (CB-4476), so the
      // transport's array→comma serialization produces the expected shape and
      // the scalar flags go through the same query builder.
      queryParams: {
        package_name: packageName,
        remove_duplicates: removeDuplicates,
        is_express: isExpress,
        is_active_sync: isActiveSync,
        publishers
      },
      deserializer: CampaignEstimateSchema,
      signal: options?.signal
    }),

  /**
   * Partial-updates campaign order state per CB-4340 (JSON Merge Patch). Only
   * fields present in the body are touched; `[]` clears an array; omitted
   * fields keep their stored value. Empty body is an idempotent no-op (200).
   *
   * BE allows the patch only when the campaign is in SAVED/CONFIRMED non-paid
   * state — 409 if it's already submitted.
   */
  patchCampaignOrder: (
    {
      campaignId,
      packageName,
      citations,
      removeDuplicates,
      isExpress,
      publishers,
      isActiveSync,
      notes,
      hideAddress
    }: PatchCampaignOrderParams,
    options?: ApiCallOptions
  ) => {
    // Build the body in camelCase and let serializeRequestBody do the single
    // snake_case pass — same idiom as the other methods. Conditional omission
    // keeps a JSON Merge Patch that only touches the fields actually provided.
    const body: Record<string, unknown> = {};

    if (packageName !== undefined) body.packageName = packageName;
    if (citations !== undefined) body.citations = citations;
    if (removeDuplicates !== undefined) body.removeDuplicates = removeDuplicates;
    if (isExpress !== undefined) body.isExpress = isExpress;
    if (publishers !== undefined) body.publishers = publishers;
    if (isActiveSync !== undefined) body.isActiveSync = isActiveSync;
    if (notes !== undefined) body.notes = notes;
    if (hideAddress !== undefined) body.hideAddress = hideAddress;

    return transport(`/manage/v1/citation-builder/${campaignId}/order`, {
      method: HTTP_METHOD.PATCH,
      body: serializeRequestBody(body),
      deserializer: PatchCampaignOrderResponseSchema,
      signal: options?.signal
    });
  },

  /**
   * Confirms the campaign order, transitioning it from SAVED to CONFIRMED
   * without taking payment (that happens on the Finalize step). Required before
   * sending the customer to the old-platform Finalize page, which 404s on a
   * still-SAVED campaign. Unlike a credits purchase, this never charges — use it
   * to close the order, then redirect to pay by card or credits.
   *
   * BE returns 409 if the campaign is already paid for; idempotent on an
   * already-confirmed order.
   */
  confirmCampaignOrder: ({ campaignId, express }: ConfirmCampaignOrderParams, options?: ApiCallOptions) => {
    const body: Record<string, unknown> = {};

    if (express !== undefined) body.express = express;

    return transport(`/manage/v1/citation-builder/${campaignId}/order/confirm`, {
      method: HTTP_METHOD.POST,
      body: serializeRequestBody(body),
      deserializer: ConfirmCampaignOrderResponseSchema,
      signal: options?.signal
    });
  },

  /**
   * Returns a single campaign's detail: the per-country aggregator catalog
   * (`availablePublishers`) and the saved order in `campaigns[]` — the flow
   * hydrates the wizard store from it on entry so a fresh device / cleared
   * storage doesn't lose the autosaved selections.
   */
  getCampaign: ({ campaignId }: GetCampaignParams, options?: ApiCallOptions) =>
    transport(`/manage/v1/citation-builder/${campaignId}`, {
      method: HTTP_METHOD.GET,
      deserializer: GetCampaignSchema,
      signal: options?.signal
    })
});

export type CitationBuilderApi = ReturnType<typeof createCitationBuilderApi>;
